import React, {useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {Table, TableBody, TableCell, TableHead, TableRow} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import DownloadIcon from '@material-ui/icons/GetApp';
import makeStyles from '@material-ui/core/styles/makeStyles';
import moment from 'moment';
import Tr from 'translations/Translations';
import DateFormats from 'constants/DateFormats';
import Dialog from 'components/Dialog';
import WorkableIconButton from 'components/button/WorkableIconButton';
import KysService from "services/KysService";
import Endpoints from "../../../constants/Endpoints";
import oneplace_kys from 'images/oneplace_kys.jpg';

const ACTION_DOWNLOAD = 'download';
const ACTION_HISTORY = 'history';

const useStyle = makeStyles(theme => ({
    titleCell: {
        fontWeight: theme.typography.fontWeightBold,
    },
    actionCell: {
        width: theme.spacing(12),
    },
    emptyCell: {
        textAlign: 'center',
    },
    dialogImage: {
        width: '100%',
        maxWidth: '600px',
        display: 'block',
        margin: '0 auto',
    },
}));

const statusTranslationKeys = {
    unverified: 'contractor_report_status_not_verified',
    verified: 'contractor_report_status_verified',
    negative: 'contractor_report_status_negative_verification',
};

const defaultStatusLabel = (status) => {
    const translationKey = statusTranslationKeys[status];
    return translationKey ? Tr(translationKey) : (status || '-');
};

const defaultFormatDateTime = (value) => {
    if (!value) return '-';
    return moment(value).format(DateFormats.DATETIME_FORMAT);
};

const normalizeVatin = (value) => (value || '').replace(/\s+/g, '');

const toHistoryItems = (response) => {
    const items = response?.result ?? response;
    return Array.isArray(items) ? items : [];
};

function ContractorReportActionButton({
    action,
    demandId,
    contractorNip,
    contractorPostCode,
    verification,
    enabled = true,
    demandHeader,
    renderStatusLabel = defaultStatusLabel,
    formatDateTime = defaultFormatDateTime,
    onReportDownloaded,
}) {
    const classes = useStyle();
    const [reportWorking, setReportWorking] = useState(false);
    const [historyWorking, setHistoryWorking] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [disabledServiceDialogOpen, setDisabledServiceDialogOpen] = useState(false);
    const [history, setHistory] = useState([]);

    const normalizedNip = useMemo(() => normalizeVatin(contractorNip), [contractorNip]);
    const canRequest = !!demandId && !!normalizedNip;
    const isKysServiceEnabled = !!demandHeader?.applicationClientSettings?.kys?.active;

    const GENERATE_REPORT_URL = verification ? Endpoints.CONTRACTOR_VERIFICATION_REPORT(demandId, normalizedNip, contractorPostCode) : Endpoints.CONTRACTOR_REPORT(demandId, normalizedNip, contractorPostCode);
    const REPORT_HISTORY_URL = verification ? Endpoints.CONTRACTOR_VERIFICATION_REPORT_HISTORY(demandId, normalizedNip, contractorPostCode) : Endpoints.CONTRACTOR_REPORT_HISTORY(demandId, normalizedNip, contractorPostCode);

    const generateReport = () => {
        if (!canRequest) return;
        if (!isKysServiceEnabled) {
            setDisabledServiceDialogOpen(true);
            return;
        }
        setReportWorking(true);
        KysService.generateReport(GENERATE_REPORT_URL, demandId, normalizedNip)
            .then(() => {
                if (typeof onReportDownloaded === 'function') {
                    return onReportDownloaded();
                }
                return null;
            })
            .finally(() => setReportWorking(false));
    };

    const openReportHistory = () => {
        if (!canRequest) return;
        setHistoryOpen(true);
        setHistoryWorking(true);
        setHistory([]);

        KysService.getReportHistory(REPORT_HISTORY_URL, demandId, normalizedNip)
            .then((response) => {
                setHistory(toHistoryItems(response));
            })
            .finally(() => setHistoryWorking(false));
    };

    const downloadHistoryReport = (historyItem) => {
        if (!canRequest || !historyItem?.identity) return;
        setReportWorking(true);

        KysService.downloadReport(verification ? Endpoints.CONTRACTOR_VERIFICATION_DOWNLOAD_REPORT(demandId, historyItem.identity) : Endpoints.CONTRACTOR_DOWNLOAD_REPORT(demandId, historyItem.identity), demandId, historyItem.identity)
            .finally(() => setReportWorking(false));
    };

    if (action === ACTION_DOWNLOAD) {
        return (
            <>
                <WorkableIconButton
                    tooltip={Tr('download_report')}
                    Icon={ShareIcon}
                    onClick={generateReport}
                    working={reportWorking}
                    disabled={(!enabled && isKysServiceEnabled) || !canRequest || historyWorking}
                />
                <Dialog
                    open={disabledServiceDialogOpen && !isKysServiceEnabled}
                    onClose={() => setDisabledServiceDialogOpen(false)}
                    maxWidth="sm"
                    title={Tr('sanctions_list')}
                >
                    <img
                        src={oneplace_kys}
                        alt={Tr('sanctions_list')}
                        className={classes.dialogImage}
                    />
                </Dialog>
            </>
        );
    }

    return (
        <>
            <WorkableIconButton
                tooltip={Tr('report_history')}
                Icon={DownloadIcon}
                onClick={openReportHistory}
                working={historyWorking}
                disabled={!enabled || !canRequest || reportWorking}
            />
            <Dialog
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                maxWidth="md"
                title={Tr('report_history')}
                loading={historyWorking}
            >
                {!isKysServiceEnabled && history.length === 0 ? (
                    <img
                        src={oneplace_kys}
                        alt={Tr('sanctions_list')}
                        className={classes.dialogImage}
                    />
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.titleCell}>{Tr('status')}</TableCell>
                                <TableCell className={classes.titleCell}>{Tr('date')}</TableCell>
                                <TableCell className={classes.titleCell}>{Tr('author')}</TableCell>
                                <TableCell className={classes.titleCell}>{Tr('download')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {history.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} className={classes.emptyCell}>{Tr('contractor_report_history_empty')}</TableCell>
                                </TableRow>
                            ) : (
                                history.map((item) => (
                                    <TableRow key={item.identity}>
                                        <TableCell>{renderStatusLabel(item.status)}</TableCell>
                                        <TableCell>{formatDateTime(item.dateInserted)}</TableCell>
                                        <TableCell>{item.author || '-'}</TableCell>
                                        <TableCell className={classes.actionCell}>
                                            {item.reportAvailable && (
                                                <WorkableIconButton
                                                    tooltip={Tr('download_report')}
                                                    Icon={DownloadIcon}
                                                    onClick={() => downloadHistoryReport(item)}
                                                    working={reportWorking}
                                                    disabled={!enabled || !canRequest || historyWorking}
                                                />
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                )}
            </Dialog>
        </>
    );
}

ContractorReportActionButton.propTypes = {
    action: PropTypes.oneOf([ACTION_DOWNLOAD, ACTION_HISTORY]).isRequired,
    demandId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    contractorNip: PropTypes.string,
    verification: PropTypes.bool,
    enabled: PropTypes.bool,
    demandHeader: PropTypes.object,
    renderStatusLabel: PropTypes.func,
    formatDateTime: PropTypes.func,
    onReportDownloaded: PropTypes.func,
};

export {ACTION_DOWNLOAD, ACTION_HISTORY};
export default ContractorReportActionButton;

